import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const unwrapLookups = (payload) => {
    const nested = payload?.results?.data || payload?.data || payload;
    if (Array.isArray(nested?.statuses)) return nested.statuses;
    if (Array.isArray(payload?.statuses)) return payload.statuses;
    if (Array.isArray(nested)) return nested;
    return [];
};

const OnsiteIssueForm = ({ issue }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const machineId = location.state?.machineId;
    const issueId = location.state?.issueId || issue?.id;
    const userID = secureLocalStorage.getItem("userID");
    const { apiClient } = useGlobalAPIQueryContext();
    const isEdit = Boolean(issue);
    const firstName = secureLocalStorage.getItem("first_name") || "";
    const lastName = secureLocalStorage.getItem("last_name") || "";
    const reporterFallback =
        [firstName, lastName].filter(Boolean).join(" ") || "Current user";

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            description: "",
            lifecycle: "Pending",
            assigned_to: "",
            tags: "",
        },
    });

    const [existingMedia, setExistingMedia] = useState(issue?.media || []);

    const { useFetch: useFetchLookups } = useAPIQuery({
        key: "api/onsite-issues/lookups",
        endpoint: "/api/onsite-issues/lookups/",
    });
    const { data: lookupResponse } = useFetchLookups({
        abortOnNewRequest: true,
        refetchOnWindowFocus: true,
        staleTime: 0,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { useCreate } = useAPIQuery({
        key: ["api/onsite-issues", machineId],
        endpoint: "/api/onsite-issues/",
    });
    const { useUpdate } = useAPIQuery({
        key: ["api/onsite-issues", machineId],
        endpoint: "/api/onsite-issues/",
    });

    const statusOptions = useMemo(() => unwrapLookups(lookupResponse), [lookupResponse]);

    const reporterName = isEdit
        ? issue?.created_by_name || reporterFallback
        : reporterFallback;

    useEffect(() => {
        if (!isEdit || !issue) return;
        reset({
            description: issue.description || "",
            lifecycle: issue.lifecycle || "Pending",
            assigned_to: issue.assigned_to_name || issue.assigned_to || "",
            tags: Array.isArray(issue.tags)
                ? issue.tags.map((tag) => tag.name || tag.value).join(", ")
                : "",
        });
        setExistingMedia(issue.media || []);
    }, [isEdit, issue, reset]);

    const afterSave = (payload, title) => {
        Swal.fire({
            title,
            text: payload?.results?.message || payload?.message || "Saved",
            icon: "success",
        }).then(() =>
            navigate("/onsite-issue-list", {
                state: { machineId, machineName: location.state?.machineName },
            })
        );
    };

    const { mutate: createIssue, isPending: creating } = useCreate({
        onSuccess: (payload) => afterSave(payload, "Issue added"),
        errorMessageFromServer: true,
        retry: 0,
    });
    const { mutate: updateIssue, isPending: updating } = useUpdate({
        onSuccess: (payload) => afterSave(payload, "Issue updated"),
        errorMessageFromServer: true,
        retry: 0,
    });

    const buildFormData = (form) => {
        const body = new FormData();
        body.append("machine_id", machineId);
        body.append("description", form.description);
        body.append("lifecycle", form.lifecycle || "Pending");
        body.append("assigned_to", form.assigned_to || "");
        body.append(
            "tags",
            JSON.stringify(
                (form.tags || "")
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter(Boolean)
            )
        );
        body.append("created_by", userID);
        body.append("updated_by", userID);
        const files = form.media;
        if (files && files.length) {
            Array.from(files).forEach((file) => body.append("media", file));
        }
        return body;
    };

    const onSubmit = (form) => {
        const body = buildFormData(form);
        if (isEdit) {
            updateIssue({ id: issueId, data: body });
        } else {
            createIssue({ data: body });
        }
    };

    const removeMedia = async (media) => {
        const confirmed = await Swal.fire({
            text: "Remove this file?",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: "No",
        });
        if (!confirmed.isConfirmed) return;
        try {
            await apiClient.delete(`/api/onsite-issues/media/${media.id}/`);
            setExistingMedia((current) => current.filter((row) => row.id !== media.id));
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Could not remove file",
                text:
                    error?.response?.data?.results?.message ||
                    error.message ||
                    "Request failed",
            });
        }
    };

    const pending = creating || updating;

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h3 className="nk-block-title page-title">
                            {isEdit ? "Edit on-site issue" : "Add on-site issue"}
                        </h3>
                    </div>
                    <NavLink
                        to="/onsite-issue-list"
                        state={{
                            machineId,
                            machineName: location.state?.machineName,
                        }}
                        className="btn btn-outline-light"
                    >
                        Back
                    </NavLink>
                </div>
            </div>
            <div className="card">
                <div className="card-inner">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row g-4">
                            <div className="col-12">
                                <label className="form-label">
                                    Description
                                    <span className="fs-8 text-danger"> *</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    placeholder="Describe the on-site problem"
                                    {...register("description", { required: true })}
                                />
                                {errors.description && (
                                    <span className="fs-8 text-danger">
                                        Description is required
                                    </span>
                                )}
                            </div>
                            <div className="col-lg-6">
                                <label className="form-label">
                                    Status
                                    <span className="fs-8 text-danger"> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    {...register("lifecycle", { required: true })}
                                >
                                    {statusOptions.length === 0 && (
                                        <option value="Pending">Pending</option>
                                    )}
                                    {statusOptions.map((row) => (
                                        <option key={row.value} value={row.value}>
                                            {row.name || row.value}
                                        </option>
                                    ))}
                                </select>
                                {errors.lifecycle && (
                                    <span className="fs-8 text-danger">Status is required</span>
                                )}
                            </div>
                            <div className="col-lg-6">
                                <label className="form-label">Reported by</label>
                                <input className="form-control" value={reporterName} readOnly />
                            </div>
                            <div className="col-lg-6">
                                <label className="form-label">Assigned to</label>
                                <input
                                    className="form-control"
                                    placeholder="Who should look into this"
                                    {...register("assigned_to")}
                                />
                            </div>
                            <div className="col-lg-6">
                                <label className="form-label">Tags</label>
                                <input
                                    className="form-control"
                                    placeholder="Comma-separated tags"
                                    {...register("tags")}
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Media</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    accept="image/*,video/*,.pdf"
                                    {...register("media")}
                                />
                            </div>
                            {existingMedia.length > 0 && (
                                <div className="col-12">
                                    <label className="form-label">Attached files</label>
                                    <ul className="list-group">
                                        {existingMedia.map((media) => (
                                            <li
                                                key={media.id}
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                <a
                                                    href={media.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {media.original_name || "File"}
                                                </a>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => removeMedia(media)}
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="col-12">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={pending}
                                >
                                    {isEdit ? "Update issue" : "Save issue"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export const CreateOnsiteIssue = () => <OnsiteIssueForm />;

export const EditOnsiteIssue = () => {
    const location = useLocation();
    const issue = location.state?.issue;
    if (!issue) {
        return <p>Issue not found.</p>;
    }
    return <OnsiteIssueForm issue={issue} />;
};

export default OnsiteIssueForm;
